import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Users,
  MessageSquare,
  RefreshCw,
  Loader2,
  Download,
} from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "stars_admin_token";

function authHeader(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function formatDate(d) {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleString("en-ZA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(d);
  }
}

function LoginCard({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter the admin password");
      return;
    }
    try {
      setLoading(true);
      const r = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      toast.success("Welcome, coach.");
      onLogin(r.data.token);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0a0a0a]">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-950 p-8 lg:p-10 space-y-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <Logo size="sm" variant="badge" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-red-600">
              Admin Console
            </p>
            <p className="font-heading uppercase tracking-tight text-xl leading-none">
              Stars of Mpumalanga
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-white/60">
            Admin Password
          </Label>
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            placeholder="••••••••"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-red-600 h-11"
          />
        </div>

        <Button
          data-testid="admin-login-button"
          type="submit"
          disabled={loading}
          className="w-full bg-red-600 hover:bg-red-500 text-white font-heading uppercase tracking-widest rounded-full h-12 text-sm"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Signing in
            </>
          ) : (
            "Enter Console"
          )}
        </Button>

        <p className="text-[11px] text-white/40 text-center">
          Authorized personnel only.
        </p>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-white/10 bg-neutral-950 p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-[0.3em] text-red-600">
          {label}
        </span>
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-600/10 text-red-600">
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="font-heading text-5xl font-bold tracking-tight">{value}</p>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [registrations, setRegistrations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({
    registrations: 0,
    contacts: 0,
    by_age_group: {},
  });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, s] = await Promise.all([
        axios.get(`${API}/admin/registrations`, authHeader(token)),
        axios.get(`${API}/admin/contacts`, authHeader(token)),
        axios.get(`${API}/admin/stats`, authHeader(token)),
      ]);
      setRegistrations(r.data || []);
      setContacts(c.data || []);
      setStats(s.data || { registrations: 0, contacts: 0, by_age_group: {} });
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [exporting, setExporting] = useState(null); // 'registrations' | 'contacts' | null

  const exportCsv = async (kind) => {
    setExporting(kind);
    try {
      const res = await fetch(`${API}/admin/${kind}.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) {
          toast.error("Session expired");
          onLogout();
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const disp = res.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="([^"]+)"/);
      const filename =
        match?.[1] || `${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filename}`);
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  const ExportButton = ({ kind, count, testid }) => (
    <Button
      data-testid={testid}
      size="sm"
      onClick={() => exportCsv(kind)}
      disabled={exporting === kind || count === 0}
      className="bg-red-600 hover:bg-red-500 text-white uppercase tracking-widest text-xs rounded-full"
    >
      {exporting === kind ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Exporting
        </>
      ) : (
        <>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </>
      )}
    </Button>
  );

  const ageGroups = Object.entries(stats.by_age_group || {});

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="sm" variant="badge" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-red-600 leading-none">
                Admin Console
              </p>
              <p className="font-heading uppercase tracking-tight text-base leading-none mt-1">
                Stars of Mpumalanga
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              data-testid="admin-refresh-button"
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-red-600 rounded-full"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <Button
              data-testid="admin-logout-button"
              size="sm"
              onClick={onLogout}
              className="bg-red-600 hover:bg-red-500 text-white rounded-full"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-10">
        <section data-testid="admin-stats">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              icon={Users}
              label="Registrations"
              value={stats.registrations}
              testid="stat-registrations"
            />
            <StatCard
              icon={MessageSquare}
              label="Messages"
              value={stats.contacts}
              testid="stat-contacts"
            />
            <div className="rounded-2xl border border-white/10 bg-neutral-950 p-6">
              <p className="text-[10px] uppercase tracking-[0.3em] text-red-600 mb-4">
                By Age Group
              </p>
              {ageGroups.length === 0 ? (
                <p className="text-white/40 text-sm">No data yet</p>
              ) : (
                <ul className="grid grid-cols-2 gap-2 text-sm">
                  {ageGroups.map(([k, v]) => (
                    <li
                      key={k}
                      className="flex justify-between border-b border-white/5 py-1"
                    >
                      <span className="text-white/60">{k}</span>
                      <span className="text-red-600 font-heading text-base">
                        {v}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        <Tabs defaultValue="registrations" className="space-y-6">
          <TabsList
            data-testid="admin-tabs"
            className="bg-neutral-900 border border-white/10"
          >
            <TabsTrigger
              value="registrations"
              data-testid="tab-registrations"
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white uppercase tracking-widest text-xs"
            >
              Registrations ({registrations.length})
            </TabsTrigger>
            <TabsTrigger
              value="contacts"
              data-testid="tab-contacts"
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white uppercase tracking-widest text-xs"
            >
              Messages ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="registrations">
            <div className="flex justify-end mb-4">
              <ExportButton
                kind="registrations"
                count={registrations.length}
                testid="export-registrations-button"
              />
            </div>
            <div className="rounded-2xl border border-white/10 bg-neutral-950 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Player
                      </TableHead>
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Age Group
                      </TableHead>
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Position
                      </TableHead>
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Parent
                      </TableHead>
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Contact
                      </TableHead>
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Submitted
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registrations.length === 0 ? (
                      <TableRow className="border-white/10">
                        <TableCell
                          colSpan={6}
                          className="text-center text-white/40 py-10"
                        >
                          No registrations yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      registrations.map((r) => (
                        <TableRow
                          key={r.id}
                          data-testid={`registration-row-${r.id}`}
                          className="border-white/10 hover:bg-white/5"
                        >
                          <TableCell className="font-medium text-white">
                            {r.player_first_name} {r.player_last_name}
                          </TableCell>
                          <TableCell>
                            <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-full bg-red-600/10 text-red-600 border border-red-600/20">
                              {r.age_group}
                            </span>
                          </TableCell>
                          <TableCell className="text-white/70">
                            {r.position || "-"}
                          </TableCell>
                          <TableCell className="text-white/70">
                            {r.parent_name}
                          </TableCell>
                          <TableCell className="text-white/70">
                            <div>{r.parent_email}</div>
                            <div className="text-xs text-white/40">
                              {r.parent_phone}
                            </div>
                          </TableCell>
                          <TableCell className="text-white/50 text-xs whitespace-nowrap">
                            {formatDate(r.created_at)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="flex justify-end mb-4">
              <ExportButton
                kind="contacts"
                count={contacts.length}
                testid="export-contacts-button"
              />
            </div>
            <div className="rounded-2xl border border-white/10 bg-neutral-950 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Name
                      </TableHead>
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Subject
                      </TableHead>
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Email
                      </TableHead>
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Message
                      </TableHead>
                      <TableHead className="text-red-600 uppercase text-[10px] tracking-widest">
                        Received
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.length === 0 ? (
                      <TableRow className="border-white/10">
                        <TableCell
                          colSpan={5}
                          className="text-center text-white/40 py-10"
                        >
                          No messages yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      contacts.map((c) => (
                        <TableRow
                          key={c.id}
                          data-testid={`contact-row-${c.id}`}
                          className="border-white/10 hover:bg-white/5"
                        >
                          <TableCell className="font-medium text-white whitespace-nowrap">
                            {c.name}
                            {c.phone && (
                              <div className="text-xs text-white/40">
                                {c.phone}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-white/70">
                            {c.subject || "-"}
                          </TableCell>
                          <TableCell className="text-white/70">
                            {c.email}
                          </TableCell>
                          <TableCell className="text-white/70 max-w-md">
                            <p className="line-clamp-3 text-sm">{c.message}</p>
                          </TableCell>
                          <TableCell className="text-white/50 text-xs whitespace-nowrap">
                            {formatDate(c.created_at)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  // If token is invalid we'll find out on first request and clear it
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    navigate("/admin");
  };

  if (!token) {
    return <LoginCard onLogin={(t) => setToken(t)} />;
  }

  return <Dashboard token={token} onLogout={handleLogout} />;
}
